import {
  Fragment,
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from "react";
import Icon from "@mdi/react";
import {
  mdiAlertCircleOutline,
  mdiChevronDown,
  mdiChevronRight,
  mdiDownload,
  mdiInformationOutline,
  mdiLoading,
  mdiRefresh,
} from "@mdi/js";
import { debounce } from "lodash";
import moment from "moment";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../context_providers/modal/loader";
import Pagination from "../../../ui/new_components/common/pagination";
import WarehouseSearchField from "../warehouse/warehouse_search_field";
import { WarehouseModel } from "../models/warehouse_model";
import {
  FetchInventoryAuditLogMovementsApi,
  FetchInventoryAuditStockLogsApi,
  InventoryAuditAction,
  InventoryAuditDeviceMovement,
  InventoryAuditStockLog,
  InventoryAuditStockLogFilter,
  InventoryAuditWacNote,
} from "./bulk_stock_upload/bulk_stock_upload_repo";

const defaultFilter = (): InventoryAuditStockLogFilter => ({
  page: 1,
  count: 20,
});

// A WAC note only needs attention when the movement could not be valued cleanly.
const WAC_NOTE_UNCLEAN = new Set([
  "skipped_no_wac",
  "skipped_no_rate",
  "clamped",
]);

const wacNoteLabel: Record<string, string> = {
  recorded: "Valued at current WAC",
  bootstrapped: "WAC seeded",
  revalued: "WAC re-averaged",
  clamped: "Closing stock floored at 0",
  skipped_no_wac: "Not valued — no WAC history",
  skipped_no_rate: "Not valued — no WAC history or PO price",
};

const hasUncleanWac = (notes?: InventoryAuditWacNote[]) =>
  (notes ?? []).some((n) => WAC_NOTE_UNCLEAN.has(n.status));

const WacNotes = ({ notes }: { notes: InventoryAuditWacNote[] }) => (
  <div className="mb-3 border rounded-md bg-white">
    <p className="px-3 py-2 text-xs font-semibold border-b bg-gray-50">
      WAC entries
    </p>
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b bg-white">
          <th className="px-3 py-2 text-left">Item Code</th>
          <th className="px-3 py-2 text-left">Direction</th>
          <th className="px-3 py-2 text-right">Qty</th>
          <th className="px-3 py-2 text-right">WAC</th>
          <th className="px-3 py-2 text-left">Outcome</th>
          <th className="px-3 py-2 text-left">Detail</th>
        </tr>
      </thead>
      <tbody>
        {notes.map((n, i) => {
          const unclean = WAC_NOTE_UNCLEAN.has(n.status);
          return (
            <tr
              id={`wac_note_row_${i}`}
              key={i}
              className={`border ${unclean ? "bg-amber-50" : ""}`}
            >
              <td className="px-3 py-2 text-left">{n.item_code}</td>
              <td
                className={`px-3 py-2 text-left font-medium ${
                  n.action === "Inward" ? "text-green-700" : "text-red-600"
                }`}
              >
                {n.action}
              </td>
              <td className="px-3 py-2 text-right">{n.qty}</td>
              <td className="px-3 py-2 text-right">
                {n.wac_value != null ? `₹${n.wac_value}` : "-"}
              </td>
              <td
                className={`px-3 py-2 text-left ${
                  unclean ? "text-amber-700 font-medium" : ""
                }`}
              >
                <span className="inline-flex items-center gap-1">
                  <Icon
                    path={
                      unclean ? mdiAlertCircleOutline : mdiInformationOutline
                    }
                    size={0.6}
                  />
                  {wacNoteLabel[n.status] ?? n.status}
                </span>
              </td>
              <td className="px-3 py-2 text-left whitespace-normal">
                {n.message ?? "-"}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const InventoryAuditStockLogsScreen: FunctionComponent = () => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<InventoryAuditStockLog[]>([]);
  const [total, setTotal] = useState(0);
  const [warehouse, setWarehouse] = useState<WarehouseModel | null>(null);
  const [filter, setFilter] =
    useState<InventoryAuditStockLogFilter>(defaultFilter());
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [movementsById, setMovementsById] = useState<
    Record<string, InventoryAuditDeviceMovement[]>
  >({});
  const [movementsLoading, setMovementsLoading] = useState<Set<string>>(
    new Set(),
  );

  const loadMovements = async (id: string) => {
    if (movementsById[id] || movementsLoading.has(id)) return;
    setMovementsLoading((prev) => new Set(prev).add(id));
    try {
      const res = await FetchInventoryAuditLogMovementsApi(id);
      if (res?.success) {
        const movements: InventoryAuditDeviceMovement[] = Array.isArray(
          res.data,
        )
          ? res.data
          : [];
        setMovementsById((prev) => ({ ...prev, [id]: movements }));
      } else {
        showToast({
          type: ToastType.error,
          text: res?.error || "Failed to fetch movement details",
        });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
    } finally {
      setMovementsLoading((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const toggleExpanded = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else {
        next.add(id);
        loadMovements(id);
      }
      return next;
    });
  };

  const fetchData = async (prop: InventoryAuditStockLogFilter) => {
    setLoading(true);
    try {
      const res = await FetchInventoryAuditStockLogsApi(prop);
      if (res?.success) {
        const logs = res.data?.logs ?? res.data?.data ?? [];
        setData(Array.isArray(logs) ? logs : []);
        setExpandedRows(new Set());
        setMovementsById({});
        const meta = res.data?.metadata;
        const totalCount = Array.isArray(meta)
          ? (meta[0]?.total ?? 0)
          : (meta?.total ?? 0);
        setTotal(totalCount);
      } else {
        showToast({
          type: ToastType.error,
          text: res?.error || "Failed to fetch audit logs",
        });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandler = useCallback(
    debounce((prop: InventoryAuditStockLogFilter) => {
      if (prop) fetchData(prop);
    }, 300),
    [],
  );

  useEffect(() => {
    debouncedHandler(filter);
  }, [filter, debouncedHandler]);

  const clearFilter = () => {
    setWarehouse(null);
    setFilter(defaultFilter());
  };

  return (
    <>
      <div
        id="page_container"
        className="flex flex-col justify-between h-full p-1"
      >
        <section className="flex justify-between items-end my-1">
          <h1 className="font-bold text-2xl text-gray-500 flex items-center gap-3">
            Stock Audit Logs
            <button
              id="refresh_btn"
              title="Refresh"
              onClick={() => fetchData(filter)}
            >
              <Icon path={mdiRefresh} className="h-6 w-6" />
            </button>
            <button
              id="clear_filter_btn"
              className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-sm text-white transform"
              onClick={clearFilter}
            >
              Clear Filter
            </button>
          </h1>
        </section>

        <section className="flex items-start justify-start m-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-5 gap-6 w-full">
            <div className="flex flex-col gap-1">
              <label className="text-md pt-1 font-semibold">Action</label>
              <select
                id="action_filter"
                className="w-full p-2 bg-white rounded focus:outline-none border"
                value={filter.action ?? ""}
                onChange={(e) =>
                  setFilter((p) => ({
                    ...p,
                    page: 1,
                    action: e.target.value || undefined,
                  }))
                }
              >
                <option value="">All</option>
                {Object.values(InventoryAuditAction)
                  .filter((a) => a !== InventoryAuditAction.item_code_transfer)
                  .map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-md pt-1 font-semibold">Warehouse</label>
              <WarehouseSearchField
                id="warehouse_filter"
                value={warehouse || undefined}
                placeholder="Select warehouse"
                onSelect={(w) => {
                  setWarehouse(w);
                  setFilter((p) => ({ ...p, page: 1, warehouse_id: w.id }));
                }}
                onClear={() => {
                  setWarehouse(null);
                  setFilter((p) => ({
                    ...p,
                    page: 1,
                    warehouse_id: undefined,
                  }));
                }}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-md pt-1 font-semibold">Item Code</label>
              <input
                id="item_code_filter"
                type="text"
                placeholder="Item code"
                className="w-full p-2 rounded focus:outline-none border"
                value={filter.item_code ?? ""}
                onChange={(e) =>
                  setFilter((p) => ({
                    ...p,
                    page: 1,
                    item_code: e.target.value || undefined,
                  }))
                }
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-md pt-1 font-semibold">From Date</label>
              <input
                id="from_date_filter"
                type="date"
                className="p-2 rounded focus:outline-none border"
                value={filter.from_date ?? ""}
                onChange={(e) =>
                  setFilter((p) => ({
                    ...p,
                    page: 1,
                    from_date: e.target.value || undefined,
                  }))
                }
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-md pt-1 font-semibold">To Date</label>
              <input
                id="to_date_filter"
                type="date"
                className="p-2 rounded focus:outline-none border"
                value={filter.to_date ?? ""}
                onChange={(e) =>
                  setFilter((p) => ({
                    ...p,
                    page: 1,
                    to_date: e.target.value || undefined,
                  }))
                }
              />
            </div>
          </div>
        </section>

        <div className="my-2 border overflow-auto" style={{ height: "80%" }}>
          <table className="w-full text-xs divide-y divide-gray-200 relative">
            <thead className="text-xs py-2 sticky -top-1 bg-white z-10">
              <tr className="border">
                <th className="px-2 py-3 w-8"></th>
                <th className="px-4 py-3 whitespace-nowrap text-left">
                  Inward/Outward Date
                </th>
                <th className="px-4 py-3 whitespace-nowrap text-left">
                  Action
                </th>
                <th className="px-4 py-3 whitespace-nowrap text-left">
                  Warehouse
                </th>
                <th className="px-4 py-3 whitespace-nowrap text-left">
                  Item Code
                </th>
                <th className="px-4 py-3 whitespace-nowrap text-left">
                  To Item Code
                </th>
                <th className="px-4 py-3 whitespace-nowrap text-right">
                  Records
                </th>
                <th className="px-4 py-3 whitespace-nowrap text-left">
                  Status
                </th>
                <th className="px-4 py-3 text-left">Remarks</th>
                <th className="px-4 py-3 whitespace-nowrap text-left">File</th>
                <th className="px-4 py-3 whitespace-nowrap text-left">
                  Created By
                </th>
                <th className="px-4 py-3 whitespace-nowrap text-left">
                  Created At
                </th>
              </tr>
            </thead>
            <tbody>
              {data?.length > 0 ? (
                data.map((row, i) => {
                  const key = row.id ?? `${i}`;
                  const wacNotes = row.wac_notes ?? [];
                  const expandable =
                    (row.device_movement_ids?.length ?? 0) > 0 ||
                    wacNotes.length > 0;
                  const isExpanded = expandedRows.has(key);
                  const movements = movementsById[key] ?? [];
                  const movementsIsLoading = movementsLoading.has(key);
                  return (
                    <Fragment key={key}>
                      <tr id={`audit_log_row_${key}`} className="border">
                        <td className="px-2 py-3 text-center">
                          {expandable ? (
                            <button
                              id={`audit_log_expand_btn_${key}`}
                              title={isExpanded ? "Collapse" : "Expand"}
                              onClick={() => toggleExpanded(key)}
                            >
                              <Icon
                                path={
                                  movementsIsLoading
                                    ? mdiLoading
                                    : isExpanded
                                      ? mdiChevronDown
                                      : mdiChevronRight
                                }
                                size={0.7}
                                spin={movementsIsLoading}
                              />
                            </button>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {row.date
                            ? moment(row.date).format("DD-MM-YYYY")
                            : "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {row.action ?? "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {row.warehouse_name ?? "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {row.item_code ?? "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {row.to_item_code ?? "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          {row.records_count ?? "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          <span className="inline-flex items-center gap-1">
                            {row.status ?? "-"}
                            {wacNotes.length > 0 && (
                              <span
                                className={
                                  hasUncleanWac(wacNotes)
                                    ? "text-amber-600"
                                    : "text-gray-400"
                                }
                                title={wacNotes
                                  .map(
                                    (n) =>
                                      n.message ??
                                      `${n.item_code}: ${
                                        wacNoteLabel[n.status] ?? n.status
                                      }`,
                                  )
                                  .join("\n")}
                              >
                                <Icon
                                  path={
                                    hasUncleanWac(wacNotes)
                                      ? mdiAlertCircleOutline
                                      : mdiInformationOutline
                                  }
                                  size={0.7}
                                />
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-left w-60 whitespace-normal break-words">
                          {row.remarks ?? "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {row.file_url ? (
                            <a
                              id={`audit_log_file_link_${key}`}
                              href={row.file_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 flex items-center gap-1 hover:text-blue-700 underline"
                            >
                              <Icon path={mdiDownload} size={0.6} />
                              {row.file_name ?? "Download"}
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {row.created_by_name ?? "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {row.created_at
                            ? moment(row.created_at).format("DD-MM-YYYY h:mm a")
                            : "-"}
                        </td>
                      </tr>
                      {expandable && isExpanded && (
                        <tr className="bg-gray-50" key={`${key}-details`}>
                          <td></td>
                          <td colSpan={11} className="px-4 py-3">
                            {wacNotes.length > 0 && (
                              <WacNotes notes={wacNotes} />
                            )}
                            {movementsIsLoading ? (
                              <div className="flex items-center justify-center gap-2 text-gray-400 py-3">
                                <Icon path={mdiLoading} size={0.8} spin />
                                <span>Loading movement details...</span>
                              </div>
                            ) : movements.length === 0 ? (
                              <div className="text-center text-gray-400 py-3">
                                No movement details found.
                              </div>
                            ) : (
                              <table className="w-full text-xs border">
                                <thead>
                                  <tr className="border bg-white">
                                    <th className="px-3 py-2 text-left">
                                      Item Code
                                    </th>
                                    <th className="px-3 py-2 text-left">
                                      Movement Type
                                    </th>
                                    <th className="px-3 py-2 text-right">
                                      Qty
                                    </th>
                                    <th className="px-3 py-2 text-left">
                                      Created At
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {movements.map((m, mi) => (
                                    <tr
                                      id={`movement_row_${key}_${mi}`}
                                      className="border"
                                      key={mi}
                                    >
                                      <td className="px-3 py-2 text-left">
                                        {m.item_code ?? "-"}
                                      </td>
                                      <td className="px-3 py-2 text-left">
                                        {m.movement_type ?? "-"}
                                      </td>
                                      <td className="px-3 py-2 text-right">
                                        {m.qty ?? "-"}
                                      </td>
                                      <td className="px-3 py-2 text-left">
                                        {m.created_at
                                          ? moment(m.created_at).format(
                                              "DD-MM-YYYY HH:mm",
                                            )
                                          : "-"}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={12}>
                    <div className="h-56 text-center p-5 my-20">
                      <h5 className="text-gray-400 text-2xl">No Data found!</h5>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div id="pagination_container" className="mt-3">
          <Pagination
            itemCount={data.length}
            page={filter.page}
            total={total}
            count={filter.count}
            onChange={(page) => setFilter((o) => ({ ...o, page }))}
          />
        </div>
      </div>
      {loading && <LoadingWidget />}
    </>
  );
};

export default InventoryAuditStockLogsScreen;
